import type { ComponentType, ReactNode } from "react";
import React, { forwardRef } from "react";

import BlockingErrorBoundary from "../components/BlockingErrorBoundary";
import type { ErrorBoundaryProps } from "../components/ErrorBoundary";
import ErrorBoundary from "../components/ErrorBoundary";

const createWithErrorBoundary =
  (ErrorBoundary: ComponentType<ErrorBoundaryProps>) =>
  <TProps extends { fallback?: ReactNode }, Element = unknown>(
    Component: ComponentType<TProps>,
    {
      fallback: defaultFallback,
      name,
      namespace,
    }: { fallback?: ReactNode; name?: string; namespace?: string },
  ) =>
    forwardRef<Element, TProps>((props, ref) => {
      return (
        <ErrorBoundary
          name={name ?? "unknown_owner_error"}
          namespace={namespace}
          fallback={props.fallback || defaultFallback}
        >
          <Component {...(props as TProps)} ref={ref} />
        </ErrorBoundary>
      );
    });

export const withBlockingErrorBoundary = createWithErrorBoundary(
  BlockingErrorBoundary,
);
export const withErrorBoundary = createWithErrorBoundary(ErrorBoundary);

export const withProviderErrorBoundary = <
  TProps extends { children: ReactNode },
>(
  Provider: ComponentType<TProps>,
  {
    fallback: defaultFallback,
    name,
    namespace,
  }: { fallback?: ReactNode; name?: string; namespace?: string },
) => {
  return (props: TProps) => (
    <ErrorBoundary
      name={name}
      namespace={namespace}
      fallback={defaultFallback || props.children}
    >
      <Provider {...props} />
    </ErrorBoundary>
  );
};
